import * as THREE from "three";
import {
  TICK_RATE,
  clampPitch,
  eyeHeightFor,
  getItemDef,
  inventoryGrids,
  stepGrenade,
  type CollisionWorld,
  type GrenadeState,
  type Vec3,
} from "@rietz/shared";
import type { GameAudio } from "../audio/synth.js";
import type { Input } from "../input/input.js";
import type { NetClock } from "../net/clock.js";
import type { Prediction } from "../net/prediction.js";
import type { GameSocket } from "../net/socket.js";
import type { Viewmodel } from "../render/viewmodel.js";
import type { Fx } from "./fx.js";
import type { InventoryController } from "./inventory.js";
import { applyLeanOffset } from "./lean.js";

/**
 * The G key, and every grenade in the air. G DOWN pulls the pin, G UP throws —
 * two messages, and the fuse starts on the first, so holding the key is
 * cooking. The client predicts NEITHER: a grenade is a slow object with a long
 * flight, so half a round trip before it appears is a few centimetres of arc,
 * and a local copy reconciled against the server's would visibly jump. The
 * thrower waits for their own `grenadeThrown` like everybody else.
 *
 * What IS local is the ARC from that event on: every client flies its own copy
 * with the shared `stepGrenade` against its own `CollisionWorld`; nothing about
 * a grenade's position is snapshotted. `grenadeExploded` carries the server's
 * word and the effect is drawn there.
 */

/** Grenades that may be on screen at once. Four players with a pin out each,
 *  twice over — a number a raid does not reach and a pool that costs nothing. */
const MAX_LIVE = 8;

/** The little dark ball, metres. Bigger than the sim's 4 cm contact radius,
 *  because a 4 cm sphere twenty metres away is not a thing anybody can see. */
const GRENADE_MESH_RADIUS_M = 0.075;
const GRENADE_MESH_COLOR = 0x353d2d;

/**
 * How long a grenade may outlive its fuse before the client gives up on it —
 * `grenadeExploded` is the real end, but a dropped event must not leave a ball
 * rolling forever.
 */
const ORPHAN_GRACE_MS = 2000;

/** One grenade being flown locally, and the ball drawn for it. */
interface LiveGrenade {
  state: GrenadeState;
  mesh: THREE.Mesh;
  /** `performance.now()` after which this is retired unheard-from. */
  expireAtMs: number;
}

export interface GrenadeControlOptions {
  /** Balls are drawn here, in world space. */
  scene: THREE.Scene;
  input: Input;
  prediction: Prediction;
  clock: NetClock;
  /** `null` offline: G does nothing at all, because nothing would answer it. */
  socket: GameSocket | null;
  /** Where the grenade to throw comes from — the displayed inventory. */
  inventory: InventoryController;
  /** Shows the ball in the off hand while a pin is out. */
  viewmodel: Viewmodel;
  fx: Fx;
  audio: GameAudio;
  /** A getter: the world is swapped wholesale on a map change. */
  world: () => CollisionWorld;
}

export class GrenadeControl {
  private readonly o: GrenadeControlOptions;

  /**
   * The iid we asked the server to arm, or `null`. Optimistic only in that it
   * drives the viewmodel's ball and gates the release; a refused arm makes the
   * release refused too.
   */
  private armedIid: string | null = null;

  /**
   * `performance.now()` the armed grenade's fuse runs out — so the optimistic
   * "pin is out" state retires when the server blows it up in the hand.
   */
  private armedUntilMs = 0;

  private dead = false;

  private readonly live: LiveGrenade[] = [];
  private readonly pool: THREE.Mesh[] = [];
  private readonly geometry: THREE.SphereGeometry;
  private readonly material: THREE.MeshLambertMaterial;

  constructor(opts: GrenadeControlOptions) {
    this.o = opts;
    this.geometry = new THREE.SphereGeometry(GRENADE_MESH_RADIUS_M, 10, 8);
    this.material = new THREE.MeshLambertMaterial({
      color: GRENADE_MESH_COLOR,
      flatShading: true,
    });
    for (let i = 0; i < MAX_LIVE; i++) {
      const mesh = new THREE.Mesh(this.geometry, this.material);
      mesh.name = "fx:grenade";
      mesh.visible = false;
      // Small, fast and always somewhere else: culling it against a bounding
      // sphere that is rewritten every frame buys nothing.
      mesh.frustumCulled = false;
      opts.scene.add(mesh);
      this.pool.push(mesh);
    }
  }

  /** True while the local player has a pin out. Drives nothing but the HUD-less
   *  viewmodel and this module's own gate; the server is the authority. */
  get isArmed(): boolean {
    return this.armedIid !== null;
  }

  /** Grenades currently being flown locally. Tests and the debug overlay. */
  get liveCount(): number {
    return this.live.length;
  }

  /**
   * One rendered frame: the key, then every arc. Both input buffers are DRAINED
   * unconditionally — a press made while dead or empty-handed is stale intent.
   */
  update(dtMs: number, nowMs: number): void {
    const presses = this.o.input.consumeGrenadePresses();
    const releases = this.o.input.consumeGrenadeReleases();

    // The fuse ran out with the pin in our hand: the server has already blown
    // it up. Empty the hand.
    if (this.armedIid !== null && nowMs >= this.armedUntilMs) this.armedIid = null;

    if (!this.dead && this.o.socket !== null) {
      if (presses > 0 && this.armedIid === null) this.arm(nowMs);
      if (releases > 0 && this.armedIid !== null) this.release(nowMs);
    }
    this.o.viewmodel.setGrenadeHeld(this.armedIid !== null);

    this.stepLive(dtMs, nowMs);
  }

  /** Death or respawn. A dead man's grenade vanishes server-side, so the hand
   *  is empty here too — see `killPlayer`. */
  setDead(dead: boolean): void {
    this.dead = dead;
    if (dead) this.armedIid = null;
  }

  /**
   * Somebody threw one — possibly us. Starts a local arc from the server's
   * launch state; a repeat of an id in flight is ignored, since the explosion's
   * lookup relies on one ball per grenade.
   */
  onThrown(
    ev: { id: string; ownerId: string; defId: string; pos: Vec3; vel: Vec3; explodeTick: number },
    nowMs: number,
  ): void {
    if (this.live.some((g) => g.state.id === ev.id)) return;

    const mesh = this.claimMesh();
    if (mesh === null) return;

    // Wall-clock time left. An unsynced clock gets the whole grace period — an
    // extra ball for two seconds beats one that vanishes mid-air.
    const ticksLeft = this.o.clock.synced
      ? Math.max(0, ev.explodeTick - this.o.clock.estServerTick(nowMs))
      : 0;
    const state: GrenadeState = {
      id: ev.id,
      ownerId: ev.ownerId,
      defId: ev.defId,
      pos: { x: ev.pos.x, y: ev.pos.y, z: ev.pos.z },
      vel: { x: ev.vel.x, y: ev.vel.y, z: ev.vel.z },
      explodeTick: ev.explodeTick,
      atRest: false,
    };
    mesh.position.set(state.pos.x, state.pos.y, state.pos.z);
    mesh.visible = true;
    this.live.push({
      state,
      mesh,
      expireAtMs: nowMs + (ticksLeft / TICK_RATE) * 1000 + ORPHAN_GRACE_MS,
    });
  }

  /**
   * One went off: the ball goes, the effects land at the SERVER's position. The
   * def is looked up here, since one cooked in somebody's hand was never
   * thrown.
   */
  onExploded(ev: { id: string; defId: string; pos: Vec3 }): void {
    const idx = this.live.findIndex((g) => g.state.id === ev.id);
    if (idx >= 0) {
      const g = this.live[idx] as LiveGrenade;
      g.mesh.visible = false;
      this.live.splice(idx, 1);
    }

    this.o.fx.explosion(ev.pos);
    this.o.audio.explosion(ev.pos);

    const def = getItemDef(ev.defId);
    if (def !== null && def.kind === "grenade" && def.effect.t === "smoke") {
      this.o.fx.smokeCloud(ev.pos, def.effect.radiusM, def.effect.durationSec);
    }
  }

  /** Leaving a raid: forget every arc and let go of the pin. The smoke on the
   *  map is `Fx`'s to clear — it outlives individual grenades. */
  reset(): void {
    for (const g of this.live) g.mesh.visible = false;
    this.live.length = 0;
    this.armedIid = null;
    this.dead = false;
    // `update` does not run without a map, so the ball is put away here
    // explicitly.
    this.o.viewmodel.setGrenadeHeld(false);
  }

  dispose(): void {
    for (const mesh of this.pool) {
      mesh.removeFromParent();
    }
    this.pool.length = 0;
    this.live.length = 0;
    this.geometry.dispose();
    this.material.dispose();
  }

  // -----------------------------------------------------------------------

  /**
   * Pulls the pin on the first grenade in canonical scan order
   * (`inventoryGrids`: pockets → rig → backpack, then grid order) — so "the
   * grenade G throws" is a thing a player can arrange.
   */
  private arm(nowMs: number): void {
    const inv = this.o.inventory.inventory;
    if (inv === null) return;

    for (const { grid } of inventoryGrids(inv)) {
      for (const placed of grid.items) {
        const def = getItemDef(placed.item.defId);
        if (def === null || def.kind !== "grenade") continue;
        this.armedIid = placed.item.iid;
        this.armedUntilMs = nowMs + def.fuseSec * 1000;
        this.o.socket?.sendGrenadeArm(placed.item.iid);
        return;
      }
    }
  }

  /**
   * Lets go: the same geometry `FireControl.fire` sends, minus every source of
   * spread — a throw has no cone, so the grenade goes exactly where the player
   * is looking.
   */
  private release(nowMs: number): void {
    this.armedIid = null;

    const synced = this.o.clock.synced;
    // No synced clock, no tick to stamp: the pin stays pulled and the grenade
    // goes off in the hand.
    if (!synced) return;

    const state = this.o.prediction.state;
    const eye: Vec3 = {
      x: state.pos.x,
      y: state.pos.y + eyeHeightFor(state.stance),
      z: state.pos.z,
    };
    // Leaned, for the reason a shot is: a grenade thrown from the unleaned eye
    // while peeking would start inside the wall being peeked around.
    applyLeanOffset(eye, this.o.input.yaw, this.o.input.lean);

    const yaw = this.o.input.yaw;
    const pitch = clampPitch(this.o.input.pitch);
    const cp = Math.cos(pitch);
    const dir: Vec3 = { x: -Math.sin(yaw) * cp, y: Math.sin(pitch), z: -Math.cos(yaw) * cp };

    this.o.socket?.sendGrenadeThrow({
      tick: this.o.clock.estServerTick(nowMs),
      origin: eye,
      dir,
    });
  }

  /** Advances every arc by one FRAME and retires the ones nobody claimed. */
  private stepLive(dtMs: number, nowMs: number): void {
    if (this.live.length === 0) return;
    // Clamped as `Fx.update` clamps: a backgrounded tab must not teleport a
    // grenade through a wall.
    const dt = Math.min(Math.max(dtMs, 0), 100) / 1000;
    const world = this.o.world();

    let write = 0;
    for (let i = 0; i < this.live.length; i++) {
      const g = this.live[i] as LiveGrenade;
      if (nowMs >= g.expireAtMs) {
        g.mesh.visible = false;
        continue;
      }
      stepGrenade(g.state, world, dt);
      g.mesh.position.set(g.state.pos.x, g.state.pos.y, g.state.pos.z);
      this.live[write++] = g;
    }
    this.live.length = write;
  }

  /** A mesh nobody is using, or `null` when all eight are in the air. */
  private claimMesh(): THREE.Mesh | null {
    for (const mesh of this.pool) {
      if (mesh.visible) continue;
      return mesh;
    }
    return null;
  }
}
